import React, { useState, useEffect, useRef, useMemo } from 'react';
import MonacoEditor from '@monaco-editor/react';
import { useParams, useNavigate } from 'react-router-dom'; 
import { useTranslation } from 'react-i18next';
import { Layout, Tree, Button, Select, Tabs, Input, Space, Divider, Card, Tag, message, Tooltip, Empty, Row, Col, Spin, Checkbox } from 'antd';
import { 
    SaveOutlined, EyeOutlined, PlusOutlined, DeleteOutlined, 
    PartitionOutlined, QrcodeOutlined, BarChartOutlined, 
    FileTextOutlined, CodeOutlined, SettingOutlined, ScissorOutlined,
    TableOutlined, ArrowLeftOutlined
} from '@ant-design/icons';
import axios from 'axios';
import api from './axiosConfig';

const { Content, Sider, Header } = Layout;
const { TabPane } = Tabs;

export default function ReportDesigner() {
    const { t, i18n } = useTranslation();
    const { id } = useParams(); 
    const navigate = useNavigate();
    const editorRef = useRef(null);
    const [activeTab, setActiveTab] = useState('body_html');
    const [loading, setLoading] = useState(false);
    
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    // 1. خريطة البيانات المتقدمة - كاملة 100% كما هي في نسختك الأصلية
    const MODEL_FIELDS = useMemo(() => ({
        WORKORDER: [
            { 
                title: t('reports.designer.master_info'), 
                key: 'wo_header', 
                children: [
                    { title: 'WO Number', key: 'main_wonum', content: '<div class="f-item"><b>Order #:</b> {{ main.wonum }}</div>', isLeaf: true },
                    { title: 'Description', key: 'main_desc', content: '<div class="f-item"><b>Description:</b> {{ main.description }}</div>', isLeaf: true },
                    { title: 'Status', key: 'main_status', content: '<span class="status-badge">{{ main.status }}</span>', isLeaf: true },
                ]
            },
            { 
                title: t('reports.designer.relational_data'), 
                key: 'wo_rel', 
                children: [
                    { title: 'Asset Name', key: 'asset_name', content: '{{ asset.description }}', isLeaf: true },
                    { title: 'Location ID', key: 'loc_id', content: '{{ location.location_id }}', isLeaf: true },
                ]
            },
            { 
                title: t('reports.designer.detail_tables'), 
                key: 'wo_tables', 
                children: [
                    { 
                        title: 'Tasks Table', 
                        key: 'tasks_table', 
                        content: `
                        <table class="rpt-table">
                            <thead>
                                <tr><th>Seq</th><th>Task Description</th><th>Status</th></tr>
                            </thead>
                            <tbody>
                                {% for t in tasks %}
                                <tr><td>{{ t.wosequence }}</td><td>{{ t.description }}</td><td>{{ t.status }}</td></tr>
                                {% endfor %}
                            </tbody>
                        </table>`, 
                        isLeaf: true 
                    },
                    { 
                        title: 'Planned Materials', 
                        key: 'mat_table', 
                        content: `
<table class="rpt-table">
    <thead>
        <tr><th>Item</th><th>Qty</th><th>Unit Cost</th></tr>
    </thead>
    <tbody>
        {% for m in planned_materials %}
        <tr><td>{{ m.itemnum }}</td><td>{{ m.quantity }}</td><td>{{ m.unitcost }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, 
                        isLeaf: true 
                    }
                ]
            }
        ],
        ASSET: [
            { title: t('reports.designer.fields.asset_basic'), key: 'as_basic', children: [
                { title: 'Asset Num', key: '{{ main.assetnum }}', isLeaf: true },
                { title: 'Install Date', key: '{{ main.install_date }}', isLeaf: true },
            ]}
        ],
        INVENTORY: [
            { title: t('reports.designer.fields.inv_basic'), key: 'inv_basic', children: [
                { title: 'Item Num', key: '{{ main.itemnum }}', isLeaf: true },
                { title: 'Storeroom', key: '{{ main.location }}', isLeaf: true },
                { title: 'Site', key: '{{ main.siteid }}', isLeaf: true },
                { title: 'Org', key: '{{ main.orgid }}', isLeaf: true },
                { title: 'Current Stock', key: '{{ main.curbal }}', isLeaf: true },
            ]}
        ],
        VENDOR: [
            { title: t('reports.designer.fields.vend_basic'), key: 'vend_basic', children: [
                { title: 'Comp Num', key: '{{ main.comp_num }}', isLeaf: true},
                { title: 'Name', key: '{{ main.name }}', isLeaf: true},
            ]}
        ],
        RFQ: [
            { title: t('reports.designer.fields.rfq_basic'), key: 'rfq_basic', children: [
                { title: 'RFQ Num', key: '{{ main.rfqnum }}', isLeaf: true},
                { title: 'Description', key: '{{ main.description }}', isLeaf: true},
            ]}
        ],
        LOCATION: [
            { title: 'Location Basic', key: 'loc_basic', children: [
                { title: 'Location ID', key: '{{ main.location_id }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Site', key: '{{ main.site_id }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Type', key: '{{ main.type }}', isLeaf: true },
                { title: 'Parent', key: '{{ main.parent_id }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'loc_tables', children: [
                { title: 'Specifications Table', key: 'loc_specs_table', content: `
<table class="rpt-table">
    <thead><tr><th>Attribute</th><th>Value</th><th>UOM</th></tr></thead>
    <tbody>
        {% for spec in specs %}
        <tr><td>{{ spec.assetattrid }}</td><td>{{ spec.alnvalue or spec.numvalue }}</td><td>{{ spec.uom }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
                { title: 'Hazards Table', key: 'loc_hazards_table', content: `
<table class="rpt-table">
    <thead><tr><th>Hazard</th><th>Type</th><th>Precaution</th></tr></thead>
    <tbody>
        {% for h in hazards %}
        <tr><td>{{ h.hazardnum }}</td><td>{{ h.hazardtype }}</td><td>{{ h.precaution }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        JOBPLAN: [
            { title: 'Job Plan Basic', key: 'jp_basic', children: [
                { title: 'JP Num', key: '{{ main.jpnum }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Duration (hrs)', key: '{{ main.duration }}', isLeaf: true },
                { title: 'Supervisor', key: '{{ main.supervisor }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'jp_tables', children: [
                { title: 'Tasks Table', key: 'jp_tasks_table', content: `
<table class="rpt-table">
    <thead><tr><th>Seq</th><th>Description</th><th>Duration</th></tr></thead>
    <tbody>
        {% for t in tasks %}
        <tr><td>{{ t.task_sequence }}</td><td>{{ t.description }}</td><td>{{ t.duration }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
                { title: 'Planned Labor Table', key: 'jp_labor_table', content: `
<table class="rpt-table">
    <thead><tr><th>Labor Code</th><th>Craft</th><th>Hours</th><th>Cost</th></tr></thead>
    <tbody>
        {% for l in labor %}
        <tr><td>{{ l.laborcode }}</td><td>{{ l.craft }}</td><td>{{ l.laborhrs }}</td><td>{{ l.linecost }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
                { title: 'Planned Materials Table', key: 'jp_materials_table', content: `
<table class="rpt-table">
    <thead><tr><th>Item</th><th>Qty</th><th>Cost</th></tr></thead>
    <tbody>
        {% for m in materials %}
        <tr><td>{{ m.itemnum }}</td><td>{{ m.quantity }}</td><td>{{ m.linecost }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        CRAFT: [
            { title: 'Craft Basic', key: 'craft_basic', children: [
                { title: 'Craft Code', key: '{{ main.craft_code }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Standard Rate', key: '{{ main.standard_rate }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'craft_tables', children: [
                { title: 'Skills Table', key: 'craft_skills_table', content: `
<table class="rpt-table">
    <thead><tr><th>Skill Level</th><th>Rate</th></tr></thead>
    <tbody>
        {% for s in skills %}
        <tr><td>{{ s.skill_level }}</td><td>{{ s.standard_rate }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        LABOR: [
            { title: 'Labor Basic', key: 'labor_basic', children: [
                { title: 'Labor Code', key: '{{ main.laborcode }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Actual Rate', key: '{{ main.actual_rate }}', isLeaf: true },
                { title: 'Person Name', key: '{{ person.displayname }}', isLeaf: true },
                { title: 'Main Craft', key: '{{ main_craft.description }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'labor_tables', children: [
                { title: 'Additional Crafts Table', key: 'labor_crafts_table', content: `
<table class="rpt-table">
    <thead><tr><th>Craft</th><th>Skill</th></tr></thead>
    <tbody>
        {% for c in crafts %}
        <tr><td>{{ c.craft_code }}</td><td>{{ c.skill_level }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        STOREROOM: [
            { title: 'Storeroom Basic', key: 'store_basic', children: [
                { title: 'Location', key: '{{ main.location }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Site', key: '{{ main.siteid }}', isLeaf: true },
                { title: 'GL Account', key: '{{ main.glaccount }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'store_tables', children: [
                { title: 'Inventory Table', key: 'store_inv_table', content: `
<table class="rpt-table">
    <thead><tr><th>Item</th><th>Description</th><th>Balance</th><th>Avg Cost</th></tr></thead>
    <tbody>
        {% for i in inventory %}
        <tr><td>{{ i.itemnum }}</td><td>{{ i.item_description }}</td><td>{{ i.curbal }}</td><td>{{ i.avgcost }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        PO: [
            { title: 'PO Basic', key: 'po_basic', children: [
                { title: 'PO Num', key: '{{ main.ponum }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Vendor', key: '{{ main.vendor }}', isLeaf: true },
                { title: 'Grand Total', key: '{{ main.grand_total }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'po_tables', children: [
                { title: 'Lines Table', key: 'po_lines_table', content: `
<table class="rpt-table">
    <thead><tr><th>Item</th><th>Description</th><th>Qty</th><th>Unit Cost</th><th>Line Cost</th></tr></thead>
    <tbody>
        {% for l in lines %}
        <tr><td>{{ l.itemnum }}</td><td>{{ l.description }}</td><td>{{ l.qty }}</td><td>{{ l.unitcost }}</td><td>{{ l.linecost }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        PR: [
            { title: 'PR Basic', key: 'pr_basic', children: [
                { title: 'PR Num', key: '{{ main.prnum }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Vendor', key: '{{ main.vendor }}', isLeaf: true },
                { title: 'Grand Total', key: '{{ main.grand_total }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'pr_tables', children: [
                { title: 'Lines Table', key: 'pr_lines_table', content: `
<table class="rpt-table">
    <thead><tr><th>Item</th><th>Description</th><th>Qty</th><th>Unit Cost</th><th>Line Cost</th></tr></thead>
    <tbody>
        {% for l in lines %}
        <tr><td>{{ l.itemnum }}</td><td>{{ l.description }}</td><td>{{ l.qty }}</td><td>{{ l.unitcost }}</td><td>{{ l.linecost }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        ITEM: [
            { title: 'Item Basic', key: 'item_basic', children: [
                { title: 'Item Num', key: '{{ main.itemnum }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Order Unit', key: '{{ main.orderunit }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'item_tables', children: [
                { title: 'Inventory Table', key: 'item_inv_table', content: `
<table class="rpt-table">
    <thead><tr><th>Location</th><th>Balance</th><th>Avg Cost</th></tr></thead>
    <tbody>
        {% for i in inventory %}
        <tr><td>{{ i.location }}</td><td>{{ i.curbal }}</td><td>{{ i.avgcost }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        PERSON: [
            { title: 'Person Basic', key: 'person_basic', children: [
                { title: 'Person ID', key: '{{ main.personid }}', isLeaf: true },
                { title: 'Display Name', key: '{{ main.displayname }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Department', key: '{{ main.department }}', isLeaf: true },
                { title: 'Main Craft', key: '{{ labor.craft_code }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'person_tables', children: [
                { title: 'Crafts Table', key: 'person_crafts_table', content: `
<table class="rpt-table">
    <thead><tr><th>Craft</th><th>Skill</th></tr></thead>
    <tbody>
        {% for c in crafts %}
        <tr><td>{{ c.craft_code }}</td><td>{{ c.skill_level }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        PM: [
            { title: 'PM Basic', key: 'pm_basic', children: [
                { title: 'PM Num', key: '{{ main.pmnum }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Asset', key: '{{ asset.assetnum }}', isLeaf: true },
                { title: 'Frequency', key: '{{ frequency.description }}', isLeaf: true },
            ]}
        ],
        SR: [
            { title: 'SR Basic', key: 'sr_basic', children: [
                { title: 'SR Num', key: '{{ main.srnum }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Reported By', key: '{{ main.reportedby }}', isLeaf: true },
                { title: 'Asset', key: '{{ main.assetnum }}', isLeaf: true },
            ]}
        ],
        MR: [
            { title: 'MR Basic', key: 'mr_basic', children: [
                { title: 'MR Num', key: '{{ main.mrnum }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Requested By', key: '{{ main.requestby }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'mr_tables', children: [
                { title: 'Lines Table', key: 'mr_lines_table', content: `
<table class="rpt-table">
    <thead><tr><th>Item</th><th>Description</th><th>Qty Requested</th><th>Qty Issued</th></tr></thead>
    <tbody>
        {% for l in lines %}
        <tr><td>{{ l.itemnum }}</td><td>{{ l.description }}</td><td>{{ l.qty_requested }}</td><td>{{ l.qty_issued }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        TOOL: [
            { title: 'Tool Basic', key: 'tool_basic', children: [
                { title: 'Tool Num', key: '{{ main.toolnum }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Tool Type', key: '{{ main.tooltype }}', isLeaf: true },
                { title: 'Manufacturer', key: '{{ main.manufacturer }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'tool_tables', children: [
                { title: 'Specifications Table', key: 'tool_specs_table', content: `
<table class="rpt-table">
    <thead><tr><th>Attribute</th><th>Value</th><th>UOM</th></tr></thead>
    <tbody>
        {% for s in specs %}
        <tr><td>{{ s.assetattrid }}</td><td>{{ s.attribute_value }}</td><td>{{ s.uom }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
                { title: 'Qualifications Table', key: 'tool_quals_table', content: `
<table class="rpt-table">
    <thead><tr><th>Qualification</th><th>Description</th></tr></thead>
    <tbody>
        {% for q in qualifications %}
        <tr><td>{{ q.qualification }}</td><td>{{ q.description }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        SAFETYPLAN: [
            { title: 'Safety Plan Basic', key: 'safety_basic', children: [
                { title: 'Plan Num', key: '{{ main.safetypnum }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Site', key: '{{ main.siteid }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'safety_tables', children: [
                { title: 'Hazards Table', key: 'safety_hazards_table', content: `
<table class="rpt-table">
    <thead><tr><th>Hazard</th><th>Type</th></tr></thead>
    <tbody>
        {% for h in hazards %}
        <tr><td>{{ h.description }}</td><td>{{ h.hazardtype }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
                { title: 'Tagouts Table', key: 'safety_tagouts_table', content: `
<table class="rpt-table">
    <thead><tr><th>Tagout</th><th>Required State</th></tr></thead>
    <tbody>
        {% for t in tagouts %}
        <tr><td>{{ t.description }}</td><td>{{ t.required_state }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
                { title: 'Work Assets Table', key: 'safety_assets_table', content: `
<table class="rpt-table">
    <thead><tr><th>Asset</th><th>Location</th><th>Description</th></tr></thead>
    <tbody>
        {% for w in work_assets %}
        <tr><td>{{ w.assetnum }}</td><td>{{ w.location }}</td><td>{{ w.description }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
                { title: 'Hazmat Table', key: 'safety_hazmat_table', content: `
<table class="rpt-table">
    <thead><tr><th>Item</th><th>Qty</th><th>Health Rating</th></tr></thead>
    <tbody>
        {% for hz in hazmat %}
        <tr><td>{{ hz.itemnum }}</td><td>{{ hz.quantity }} {{ hz.unit }}</td><td>{{ hz.health_rating }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        INCIDENT: [
            { title: 'Incident Basic', key: 'incident_basic', children: [
                { title: 'Ticket ID', key: '{{ main.ticket_id }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Priority', key: '{{ main.priority }}', isLeaf: true },
                { title: 'Reported By', key: '{{ main.reported_by }}', isLeaf: true },
                { title: 'Asset', key: '{{ main.asset.description }}', isLeaf: true },
                { title: 'Location', key: '{{ main.location.description }}', isLeaf: true },
            ]}
        ],
        CONTRACTS: [
            { title: 'Contract Basic', key: 'contract_basic', children: [
                { title: 'Contract Num', key: '{{ main.contract_num }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Vendor', key: '{{ main.vendor_id }}', isLeaf: true },
                { title: 'Total Amount', key: '{{ main.total_amount }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'contract_tables', children: [
                { title: 'Terms Table', key: 'contract_terms_table', content: `
<table class="rpt-table">
    <thead><tr><th>Clause Type</th><th>Description</th></tr></thead>
    <tbody>
        {% for t in terms %}
        <tr><td>{{ t.clause_type }}</td><td>{{ t.description }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
                { title: 'Lines Table', key: 'contract_lines_table', content: `
<table class="rpt-table">
    <thead><tr><th>Item</th><th>Description</th><th>Qty</th><th>Unit Price</th><th>Line Total</th></tr></thead>
    <tbody>
        {% for l in lines %}
        <tr><td>{{ l.itemnum }}</td><td>{{ l.description }}</td><td>{{ l.qty }}</td><td>{{ l.unit_price }}</td><td>{{ l.line_total }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        SLA: [
            { title: 'SLA Basic', key: 'sla_basic', children: [
                { title: 'SLA Num', key: '{{ main.slanum }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Type', key: '{{ main.sla_type }}', isLeaf: true },
                { title: 'Applies To', key: '{{ main.applies_to }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'sla_tables', children: [
                { title: 'Commitments Table', key: 'sla_commitments_table', content: `
<table class="rpt-table">
    <thead><tr><th>Type</th><th>Description</th><th>Value</th></tr></thead>
    <tbody>
        {% for c in commitments %}
        <tr><td>{{ c.type }}</td><td>{{ c.description }}</td><td>{{ c.value }} {{ c.unit_of_measure }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
                { title: 'Escalations Table', key: 'sla_escalations_table', content: `
<table class="rpt-table">
    <thead><tr><th>Description</th></tr></thead>
    <tbody>
        {% for e in escalations %}
        <tr><td>{{ e.description }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ],
        INVTRANS: [
            { title: 'Transactions Table (Summary Report)', key: 'invtrans_table', content: `
<table class="rpt-table">
    <thead><tr><th>Date</th><th>Item</th><th>Description</th><th>Type</th><th>Qty</th><th>Location</th><th>Line Cost</th></tr></thead>
    <tbody>
        {% for r in records %}
        <tr><td>{{ r.transdate }}</td><td>{{ r.itemnum }}</td><td>{{ r.item_description }}</td><td>{{ r.transtype }}</td><td>{{ r.quantity }}</td><td>{{ r.location }}</td><td>{{ r.linecost }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
        ],
        INVUSAGE: [
            { title: 'Usage Basic', key: 'invusage_basic', children: [
                { title: 'Usage Num', key: '{{ main.invusenum }}', isLeaf: true },
                { title: 'Description', key: '{{ main.description }}', isLeaf: true },
                { title: 'Status', key: '{{ main.status }}', isLeaf: true },
                { title: 'Type', key: '{{ main.usage_type }}', isLeaf: true },
                { title: 'From Store', key: '{{ main.fromstore }}', isLeaf: true },
                { title: 'To Store', key: '{{ main.tostore }}', isLeaf: true },
                { title: 'Ref MR', key: '{{ main.ref_mr }}', isLeaf: true },
            ]},
            { title: 'Detail Tables', key: 'invusage_tables', children: [
                { title: 'Lines Table', key: 'invusage_lines_table', content: `
<table class="rpt-table">
    <thead><tr><th>Item</th><th>Description</th><th>Qty</th><th>Unit Cost</th><th>Line Cost</th><th>WO Ref</th><th>Asset</th></tr></thead>
    <tbody>
        {% for l in lines %}
        <tr><td>{{ l.itemnum }}</td><td>{{ l.item_description }}</td><td>{{ l.quantity }}</td><td>{{ l.unitcost }}</td><td>{{ l.linecost }}</td><td>{{ l.wonum }}</td><td>{{ l.assetnum }}</td></tr>
        {% endfor %}
    </tbody>
</table>`, isLeaf: true },
            ]}
        ]
    }), [t]);

    // 🎯 الحالة الشاملة للتقرير
    const [report, setReport] = useState({
        name: "Professional Work Order Report",
        ref_object: "WORKORDER",
        header_html: "<div class='report-header'>\n  <h2>TEKNORA ASSET MANAGEMENT</h2>\n  <p>Date: {{ print_date }}</p>\n</div>",
        body_html: "<div class='report-body'>\n  \n</div>",
        footer_html: "<div class='report-footer'>\n  <p>Page <span class='pageNumber'></span></p>\n</div>",
        custom_css: ".rpt-table { width:100%; border-collapse:collapse; } \n.rpt-table th { background:#005495; color:#fff; padding:8px; } \n.rpt-table td { border:1px solid #ddd; padding:8px; }",
        parameters_config: [] 
    });

    useEffect(() => {
        if (id && id !== 'new') {
            setLoading(true);
            axios.get(`/api/reports/template-detail/${id}`)
                .then(res => {
                    const data = res.data;
                    setReport({
                        ...data,
                        parameters_config: data.parameters_config || []
                    });
                })
                .catch(err => {
                    message.error(t('reports.toasts.load_fail') + err.message);
                })
                .finally(() => setLoading(false));
        } else {
            setReport({
                name: "New Report Template",
                ref_object: "WORKORDER",
                header_html: "<h2>Report Header</h2>",
                body_html: "",
                footer_html: "<p>Page footer</p>",
                custom_css: ".rpt-table { width: 100%; }",
                parameters_config: []
            });
        }
    }, [id, t]);

    const injectCode = (content) => {
        if (editorRef.current) {
            const editor = editorRef.current;
            const selection = editor.getSelection();
            editor.executeEdits("maxeam-designer", [{ 
                range: selection, 
                text: content, 
                forceMoveMarkers: true 
            }]);
        } else {
            message.warning(t('studio.console.init'));
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const res = await axios.post('/api/reports/report-templates/save', report);
            message.success(t('reports.toasts.save_success'));
            if (!id || id === 'new') {
                navigate(`/reportdesigner/${res.data.id}`);
            }
        } catch (err) {
            message.error(t('reports.toasts.save_fail') + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = () => {
        const previewData = {
            ...report,
            is_preview: true,
            ref_id: null
        };
        axios.post('/api/reports/preview-session', previewData).then(res => {
            window.open(`/api/reports/preview?session_id=${res.data.session_id}&preview=true`, '_blank');
        });
    };

    // 🎯 تطبيقات الكستم مالهاش شجرة حقول ثابتة زي WORKORDER/ASSET فوق -
    // بنجيبها ديناميكيًا من ميتا الموديل نفسه (نفس حقول {{ main.<field_name> }}
    // اللي fetch_deep_data الجديد بيرجعها في data_fetcher.py)
    const [customModels, setCustomModels] = useState([]);
    const [customTree, setCustomTree] = useState([]);

    // 🎯 /custom-models محمي بصلاحية (custom_apps.read) على عكس باقي
    // endpoints الملف ده (template-detail, report-templates/save..) اللي
    // من غير أي تحقق دخول - axios الخام هنا مبيبعتش Authorization header
    // خالص (مفيش axios.defaults في المشروع كله)، فلازم نستخدم instance
    // "api" (axiosConfig.js) اللي بيحقن التوكن تلقائي، وإلا بترجع 401
    useEffect(() => {
        api.get('/custom-models').then(res => setCustomModels(res.data || [])).catch(() => setCustomModels([]));
    }, []);

    useEffect(() => {
        if (!report.ref_object?.startsWith('CUSTOM_')) { setCustomTree([]); return; }
        const slug = report.ref_object.slice('CUSTOM_'.length).toLowerCase();
        api.get(`/custom-models/${slug}`).then(res => {
            const fields = res.data?.fields || [];
            setCustomTree([{
                title: t('reports.designer.master_info'),
                key: 'custom_fields',
                children: fields.map(f => ({
                    title: f.label,
                    key: `custom_${f.field_name}`,
                    content: `{{ main.${f.field_name} }}`,
                    isLeaf: true,
                })),
            }]);
        }).catch(() => setCustomTree([]));
    }, [report.ref_object, t]);

    const addParam = () => {
        const config = report.parameters_config || [];
        const newParam = { name: `p${config.length + 1}`, label: 'New Parameter', type: 'text' };
        setReport({...report, parameters_config: [...config, newParam]});
    };

    const updateParam = (index, field, value) => {
        const updated = [...(report.parameters_config || [])];
        updated[index][field] = value;
        setReport({...report, parameters_config: updated});
    };

    const deleteParam = (index) => {
        const updated = (report.parameters_config || []).filter((_, i) => i !== index);
        setReport({...report, parameters_config: updated});
    };

    if (loading) return <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spin size="large" tip={t('workflow.loading')} /></div>;

    return (
        <Layout style={{ height: '100vh', background: '#fff' }} className={isRTL ? 'font-cairo' : 'font-sans'} dir={isRTL ? 'rtl' : 'ltr'}>
            <Header style={{ background: '#001529', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px' }}>
                <Space>
                    <Button ghost icon={<ArrowLeftOutlined className={isRTL ? 'rotate-180' : ''} />} onClick={() => navigate('/reportlist')}>{t('common.back')}</Button>
                    <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>
                        <SettingOutlined /> {t('reports.designer.title')}
                    </div>
                </Space>
                <Space>
                    <Button icon={<EyeOutlined />} onClick={handlePreview}>{t('reports.designer.live_preview')}</Button>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>{t('reports.designer.publish')}</Button>
                </Space>
            </Header>

            <Layout className="overflow-hidden">
                <Sider width={320} theme="light" style={{ borderRight: isRTL ? 'none' : '1px solid #f0f0f0', borderLeft: isRTL ? '1px solid #f0f0f0' : 'none', overflowY: 'auto', padding: '15px' }}>
                    <h4 className="font-bold text-sm mb-2">{t('reports.designer.model_select')}</h4>
                    <Select style={{ width: '100%' }} value={report.ref_object} onChange={v => setReport({...report, ref_object: v})}>
                        <Select.Option value="WORKORDER">Work Order Hub</Select.Option>
                        <Select.Option value="ASSET">Asset Registry</Select.Option>
                        <Select.Option value="INVENTORY">Inventory Snapshot</Select.Option>
                        <Select.Option value="VENDOR">Vendor</Select.Option>
                        <Select.Option value="RFQ">RFQ</Select.Option>
                        <Select.Option value="LOCATION">Location</Select.Option>
                        <Select.Option value="JOBPLAN">Job Plan</Select.Option>
                        <Select.Option value="CRAFT">Craft</Select.Option>
                        <Select.Option value="LABOR">Labor</Select.Option>
                        <Select.Option value="STOREROOM">Storeroom</Select.Option>
                        <Select.Option value="PO">Purchase Order</Select.Option>
                        <Select.Option value="PR">Purchase Request</Select.Option>
                        <Select.Option value="ITEM">Item Master</Select.Option>
                        <Select.Option value="PERSON">Person</Select.Option>
                        <Select.Option value="PM">Preventive Maintenance</Select.Option>
                        <Select.Option value="SR">Service Request</Select.Option>
                        <Select.Option value="MR">Material Request</Select.Option>
                        <Select.Option value="TOOL">Tool</Select.Option>
                        <Select.Option value="SAFETYPLAN">Safety Plan</Select.Option>
                        <Select.Option value="INCIDENT">Incident</Select.Option>
                        <Select.Option value="CONTRACTS">Contract</Select.Option>
                        <Select.Option value="SLA">SLA</Select.Option>
                        <Select.Option value="INVTRANS">Inventory Transactions</Select.Option>
                        <Select.Option value="INVUSAGE">Inventory Usage (Issue/Transfer)</Select.Option>
                        {customModels.length > 0 && (
                            <Select.OptGroup label={t('reports.designer.customApps', 'Custom Applications')}>
                                {customModels.map(m => (
                                    <Select.Option key={m.slug} value={`CUSTOM_${m.slug.toUpperCase()}`}>{m.label}</Select.Option>
                                ))}
                            </Select.OptGroup>
                        )}
                    </Select>

                    <Divider className="my-4" />

                    <h4 className="font-bold text-sm mb-2"><PartitionOutlined /> {t('reports.designer.data_explorer')}</h4>
                    <Tree
                        showLine
                        treeData={report.ref_object?.startsWith('CUSTOM_') ? customTree : (MODEL_FIELDS[report.ref_object] || [])}
                        onSelect={(keys, info) => { if(info.node.isLeaf) injectCode(info.node.content) }}
                    />

                    <Divider className="my-4" />

                    <h4 className="font-bold text-sm mb-2"><ScissorOutlined /> {t('reports.designer.components')}</h4>
                    <div className="grid grid-cols-2 gap-2">
                        <Button size="small" icon={<QrcodeOutlined />} onClick={() => injectCode("<qr-code data='{{ main.wonum }}' size='100'></qr-code>")}>QR Code</Button>
                        <Button size="small" icon={<TableOutlined />} onClick={() => injectCode("<div style='page-break-after: always;'></div>")}>{t('reports.designer.page_break')}</Button>
                    </div>
                    
                    <Divider className="my-4" />
                    
                    <div style={{ marginBottom: 15, padding: '10px', background: '#f9f9f9', borderRadius: '4px' }}>
                        <Checkbox 
                            checked={report.is_summary} 
                            onChange={e => setReport({...report, is_summary: e.target.checked})}
                        >
                            <b>{t('reports.designer.is_summary')}</b>
                        </Checkbox>
                    </div>

                    <Divider className="my-4" />

                    <h4 className="font-bold text-sm mb-2">{t('reports.designer.user_params')}</h4>
                    {(report.parameters_config || []).map((p, idx) => (
                        <Card size="small" key={idx} className="mb-2 bg-gray-50" 
                            extra={<DeleteOutlined className="text-red-500 cursor-pointer" onClick={() => deleteParam(idx)} />}>
                            <Input size="small" placeholder={t('field_config.field_name')} value={p.name} onChange={e => updateParam(idx, 'name', e.target.value)} />
                            <Input size="small" placeholder={t('workflow.node_label')} value={p.label} className="mt-1" onChange={e => updateParam(idx, 'label', e.target.value)} />
                            <Select size="small" className="w-full mt-1" value={p.type} onChange={v => updateParam(idx, 'type', v)}>
                                <Select.Option value="text">Text Input</Select.Option>
                                <Select.Option value="dropdown">Lookup (Search)</Select.Option>
                                <Select.Option value="domain">DOMAIN</Select.Option>
                                <Select.Option value="date">Date Picker</Select.Option>
                            </Select>

                            {p.type === 'dropdown' && (
                                <Select size="small" placeholder="Select Lookup Module" className="w-full mt-1" value={p.lookupType} onChange={v => updateParam(idx, 'lookupType', v)}>
                                    <Select.Option value="sites">Sites</Select.Option>
                                    <Select.Option value="storerooms">Storerooms</Select.Option>
                                    <Select.Option value="assets">Assets</Select.Option>
                                    <Select.Option value="items">Item Master</Select.Option>
                                    <Select.Option value="workorder">Work Orders</Select.Option>
                                    <Select.Option value="companies">Vendors</Select.Option>
                                </Select>
                            )}
                            {p.type === 'domain' && (
                                <Select size="small" placeholder="Select Domain" className="w-full mt-1" value={p.lookupType} onChange={v => updateParam(idx, 'lookupType', v)}>
                                    <Select.Option value="assettype">ASSET TYPE</Select.Option>
                                    <Select.Option value="country">COUNTRY</Select.Option>
                                    <Select.Option value="metertype">METER TYPE</Select.Option>
                                    <Select.Option value="loctype">LOCATION TYPE</Select.Option>
                                    <Select.Option value="wotype">WORKORDER TYPE</Select.Option>
                                    <Select.Option value="wostatus">WORKORDER STATUS</Select.Option>
                                    <Select.Option value="assetstatus">ASSET STATUS</Select.Option>
                                    <Select.Option value="uom">UNIT OF MISURES</Select.Option>
                                </Select>
                            )}
                        </Card>
                    ))}
                    <Button type="dashed" block icon={<PlusOutlined />} onClick={addParam} className="mt-2">{t('reports.designer.add_param')}</Button>
                </Sider>

                <Content style={{ display: 'flex', flexDirection: 'column' }} className="bg-[#0b0e14]">
                    <div className="p-4 bg-white border-b flex items-center">
                        <Input variant="borderless" value={report.name} onChange={e => setReport({...report, name: e.target.value})} style={{ fontWeight: 'bold', fontSize: '18px' }} />
                    </div>

                    <Tabs activeKey={activeTab} onChange={setActiveTab} className="px-4 bg-white shrink-0"
                        items={[
                            { label: t('reports.designer.tabs.body'), key: 'body_html', icon: <CodeOutlined /> },
                            { label: t('reports.designer.tabs.header'), key: 'header_html', icon: <FileTextOutlined /> },
                            { label: t('reports.designer.tabs.footer'), key: 'footer_html', icon: <FileTextOutlined /> },
                            { label: t('reports.designer.tabs.css'), key: 'custom_css', icon: <SettingOutlined /> },
                        ]} 
                    />

                    <div style={{ flex: 1 }} dir="ltr">
                        <MonacoEditor
                            path={activeTab}
                            height="100%"
                            language={activeTab === 'custom_css' ? 'css' : 'html'}
                            theme="vs-dark"
                            value={report[activeTab] || ""}
                            onMount={(editor) => { editorRef.current = editor; }}
                            onChange={(val) => setReport({...report, [activeTab]: val})}
                            options={{ fontSize: 14, minimap: { enabled: false }, automaticLayout: true }}
                        />
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
}